import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: sp.get("role") === "seller" ? "seller" : "buyer",
    shop_name: "",
    address: { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" },
    id_document_key: "",
    id_document_filename: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadId = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/id-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, id_document_key: data.key, id_document_filename: file.name }));
      toast.success("ID document uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "seller") {
      const a = form.address;
      if (!a.line1.trim() || !a.city.trim() || !a.country.trim()) {
        toast.error("Address (line 1, city, country) is required"); return;
      }
      if (!form.id_document_key) {
        toast.error("Please upload a government ID or passport page"); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, email: form.email, password: form.password, role: form.role,
        shop_name: form.role === "seller" ? form.shop_name : null,
        address: form.role === "seller" ? form.address : null,
        id_document_key: form.role === "seller" ? form.id_document_key : null,
      };
      const u = await register(payload);
      toast.success(`Welcome to Jubasmash, ${u.name}`);
      nav(u.role === "seller" ? "/seller" : "/buyer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  const setAddr = (k, v) => setForm({ ...form, address: { ...form.address, [k]: v } });

  return (
    <Layout>
      <section className="max-w-lg mx-auto px-6 py-16">
        <h1 className="heading text-4xl font-black uppercase tracking-tight">Create account</h1>
        <p className="text-[#555] mt-2">Join the marketplace.</p>

        <div className="mt-6 grid grid-cols-2 brut-border">
          <button data-testid="role-buyer-tab" onClick={() => setForm({ ...form, role: "buyer" })} className={`py-3 mono uppercase text-xs tracking-[0.25em] ${form.role === "buyer" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>I'm buying</button>
          <button data-testid="role-seller-tab" onClick={() => setForm({ ...form, role: "seller" })} className={`py-3 mono uppercase text-xs tracking-[0.25em] border-l-2 border-[#1F3D2A] ${form.role === "seller" ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>I'm selling</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Name</label>
            <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>
          {form.role === "seller" && (
            <div>
              <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Shop name</label>
              <input data-testid="signup-shop" required value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
            </div>
          )}
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Email</label>
            <input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>
          <div>
            <label className="mono text-xs uppercase tracking-[0.2em] font-bold">Password</label>
            <input data-testid="signup-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-2 brut-border bg-white px-4 py-3" />
          </div>

          {form.role === "seller" && (
            <div className="brut-border bg-[#FDF3E9] p-5 space-y-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Seller address</div>
                <p className="text-xs text-[#555] mt-1">Used for shipping and as the local pickup location on your products.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Address line 1 *</label>
                  <input data-testid="signup-addr-line1" required value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Address line 2</label>
                  <input data-testid="signup-addr-line2" value={form.address.line2} onChange={(e) => setAddr("line2", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">City *</label>
                  <input data-testid="signup-addr-city" required value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">State / Region</label>
                  <input data-testid="signup-addr-state" value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Postal code</label>
                  <input data-testid="signup-addr-postal" value={form.address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Country *</label>
                  <input data-testid="signup-addr-country" required value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="pt-3 border-t-2 border-dashed border-[#EAEAEA]">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Identity verification</div>
                <p className="text-xs text-[#555] mt-1">Upload a photo of your government-issued ID or passport page. Only Jubasmash admins can view it — never other users.</p>
                <input data-testid="signup-id-upload" type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && uploadId(e.target.files[0])} className="w-full mt-3 brut-border bg-white px-3 py-2 text-sm" />
                {uploading && <div className="mono text-xs text-[#555] mt-2">Uploading…</div>}
                {form.id_document_key && (
                  <div className="mt-2 mono text-xs text-[#1F3D2A]" data-testid="signup-id-uploaded">
                    ✓ Uploaded: {form.id_document_filename}
                  </div>
                )}
              </div>
            </div>
          )}

          <button data-testid="signup-submit-btn" disabled={busy || uploading} className="btn-primary w-full">{busy ? "Creating…" : "Create account"}</button>
        </form>
        <p className="mt-6 mono text-xs uppercase tracking-[0.2em] text-[#555]">
          Have an account? <Link to="/login" className="text-[#C77E3C]">Sign in →</Link>
        </p>
      </section>
    </Layout>
  );
}
